import _ from "lodash";
import pako from "pako";

import {
  api,
  fetchRespJson,
  postData,
  fetchRespJsonWithoutCredentials,
} from "apis/base";

export default {
  // network
  getNetworkResources,
  getNetworkInfo,
  getUnitsByMode,
  getUnitsByUnitIds,
  updateUnitsByStatus,
  getNetworksInfo,
  getNetworkCustomUpr,
  getNetworkCustomUprChangeHistory,
  getNetworkCustomFirstLook,
  updateNetworkCustomFirstLook,
  getNetworkCustomFirstLookChangeHistory,
  getUnitsByNetworkId,
  updateNetworkCustomUpr,
  updateNetworkAdServingCost,
  getNetworks,
  getYieldSets,
  getNetworkGamInfo,
  syncGamNetwork,
  verifyGamNetwork,
  getNetworkCompetitorSummary,
  getLatestTwdConvertRate,

  getNetworkUprSegmentation,
  getNetworkUprSegmentationChangeHistory,
  getNetworkUprSegmentationByUnitId,
  getInventoryRegistry,
  createNetworkUprSegmentation,
  getFirstLayerPriceSuggestion,
  getCstConfig,
  getCstConfigV2,
  getCstConfigChangeHistory,
  createCstConfig,

  getCustomTargetingKeys,
  getCustomTargetingValues,
  getCstEstimateResource,
  getRegisteredInventorySuggestion,

  getFeaturesSchema,
  getDefaultNetworkFeatures,
  getRawNetworkFeatures,
  createNetworkFeatures,
  getRawPublisherFeatures,
  createPublisherFeatures,

  getCodeSnippetBuilds,
  getBuildDetails,
  getBuildInput,
  getBuildPackages,
  createNewBuild,
  createTempBuild,
  createBuildComparison,
  syncCloudFlareAmpRTC,
  updateBuildNotes,
  updateIntegrationReleaseTime,
  updateProductionReleaseTime,

  triggerEnsureResources,

  getNetworkResourceMapping,
  getExtGamUprPriceTargetingCandidates,

  getNetworkVirtualPlacements,
  createNetworkVirtualPlacement,
  updateNetworkVirtualPlacement,
  deleteNetworkVirtualPlacement,

  getNetworkAdvertiserAndBrands,

  getDiagnoserReport,
  getDiagnoserSymptoms,

  getPrebidSettings,
  triggerUpdatePrebidCache,
  getPrebidSummary,

  getGamUprs,
  getGamUpr,
  getGamUprByIds,
  getGamUprChangeLogs,
  getFirstLookRules,
  getFirstLookRulesByIds,

  updateNetworkBillableTypes,
  updateNetworkStatus,

  validateCustomUprSetting,
  validateCustomFirstLookSetting,
  validateCstConfig,
  validateUprSegmentation,

  getNetworkSensitiveCategories,
  getNetworkInventoryUrls,
  getNetworkMobileApplications,
  getNetworkInventorySizes,
  getNetworkDemandSources,

  getNetworkSnapshotTrend,
};

async function getNetworkDemandSources({ networkId }) {
  let apiUrl = `${api}/network/${networkId}/demand-sources`;
  return await fetchRespJson(apiUrl);
}

async function getLatestTwdConvertRate({ networkId }) {
  let apiUrl = `${api}/network/${networkId}/latest-twd-convert-rate`;
  return await fetchRespJson(apiUrl);
}

async function getNetworkInventorySizes({ networkId }) {
  let apiUrl = `${api}/network/${networkId}/inventory-sizes`;
  return await fetchRespJson(apiUrl);
}

async function getNetworkMobileApplications({ networkId }) {
  let apiUrl = `${api}/network/${networkId}/mobile-applications`;
  return await fetchRespJson(apiUrl);
}

async function getNetworkInventoryUrls({ networkId }) {
  let apiUrl = `${api}/network/${networkId}/inventory-urls`;
  return await fetchRespJson(apiUrl);
}

async function getNetworkSensitiveCategories({ networkId }) {
  let apiUrl = `${api}/network/${networkId}/sensitive-categories`;
  return await fetchRespJson(apiUrl);
}

async function getNetworkCompetitorSummary({ networkId }) {
  let apiUrl = `${api}/network/${networkId}/competitor-summary`;
  return await fetchRespJson(apiUrl);
}

async function verifyGamNetwork(params) {
  const apiUrl = `${api}/network/${params.networkId}/verify-gam-network`;
  return await postData(apiUrl, params);
}

async function syncGamNetwork(params) {
  const apiUrl = `${api}/network/${params.networkId}/sync-gam-network`;
  return await postData(apiUrl, params);
}

async function updateNetworkBillableTypes({ params }) {
  let apiUrl = `${api}/network/${params.networkId}/update-billable-types`;
  return await postData(apiUrl, params);
}

async function updateNetworkStatus(params) {
  let apiUrl = `${api}/network/${params.networkId}/update-network-status`;
  return await postData(apiUrl, params);
}

async function getPrebidSummary({ networkId }) {
  let apiUrl = `${api}/network/${networkId}/prebid-summary`;

  const { url } = await fetchRespJson(apiUrl);
  const data = await fetchRespJsonWithoutCredentials(url);
  return data;
}

async function triggerUpdatePrebidCache({ networkId }) {
  let apiUrl = `${api}/network/${networkId}/trigger-update-prebid-cache`;
  return await fetchRespJson(apiUrl);
}

async function getGamUpr({ networkId, uprId }) {
  let apiUrl = `${api}/network/${networkId}/gam-upr?uprId=${uprId}`;
  return await fetchRespJson(apiUrl);
}

// status: ACTIVE, INACTIVE
async function getGamUprs({ networkId, isForceRefresh, status }) {
  let apiUrl = `${api}/network/${networkId}/gam-upr-list`;
  let queryParams = [];
  if (isForceRefresh) {
    queryParams.push("isForceRefresh=true");
  }
  if (status) {
    queryParams.push(`status=${status}`);
  }
  if (queryParams.length > 0) {
    apiUrl += "?" + queryParams.join("&");
  }
  return await fetchRespJson(apiUrl);
}

// status: ACTIVE, INACTIVE
async function getGamUprByIds({ networkId, externalIds }) {
  let apiUrl = `${api}/network/${networkId}/gam-upr-by-ids`;
  let queryParams = [];
  if (externalIds && externalIds.length > 0) {
    queryParams.push(`externalIds=${externalIds.join(",")}`);
  }
  if (queryParams.length > 0) {
    apiUrl += "?" + queryParams.join("&");
  }
  return await fetchRespJson(apiUrl);
}

async function getGamUprChangeLogs(params) {
  const apiUrl = `${api}/network/${params.networkId}/gam-upr-change-logs`;
  return await postData(apiUrl, params);
}

async function getFirstLookRules({ networkId, isForceRefresh, status }) {
  let apiUrl = `${api}/network/${networkId}/first-look-rules`;
  let queryParams = [];
  if (isForceRefresh) {
    queryParams.push("isForceRefresh=true");
  }
  if (status) {
    queryParams.push(`status=${status}`);
  }
  if (queryParams.length > 0) {
    apiUrl += "?" + queryParams.join("&");
  }
  return await fetchRespJson(apiUrl);
}

async function getFirstLookRulesByIds({ networkId, externalIds }) {
  let apiUrl = `${api}/network/${networkId}/first-look-rules-by-ids`;
  let queryParams = [];
  if (externalIds && externalIds.length > 0) {
    queryParams.push(`externalIds=${externalIds.join(",")}`);
  }
  if (queryParams.length > 0) {
    apiUrl += "?" + queryParams.join("&");
  }
  return await fetchRespJson(apiUrl);
}

async function getPrebidSettings({ networkId }) {
  let apiUrl = `${api}/network/${networkId}/prebid-settings`;
  return await fetchRespJson(apiUrl);
}

async function getDiagnoserSymptoms() {
  let apiUrl = `${api}/diagnoser-symptoms`;
  return await fetchRespJson(apiUrl);
}

async function getDiagnoserReport({ networkId, symptomName, reportDate }) {
  let apiUrl = `${api}/network/${networkId}/symptom-report?symptomName=${symptomName}&reportDate=${reportDate}`;
  return await fetchRespJson(apiUrl);
}

async function getNetworkAdvertiserAndBrands(params) {
  let apiUrl = `${api}/network/${params.networkId}/advertiser-and-brands`;
  return await postData(apiUrl, params);
}

async function deleteNetworkVirtualPlacement(params) {
  let apiUrl = `${api}/network/${params.networkId}/delete-virtual-placement`;
  return await postData(apiUrl, params);
}

async function updateNetworkVirtualPlacement(params) {
  let apiUrl = `${api}/network/${params.networkId}/update-virtual-placement`;
  return await postData(apiUrl, params);
}

async function createNetworkVirtualPlacement(params) {
  let apiUrl = `${api}/network/${params.networkId}/create-virtual-placement`;
  return await postData(apiUrl, params);
}

async function getNetworkVirtualPlacements({ networkId }) {
  let apiUrl = `${api}/network/${networkId}/virtual-placements`;
  return await fetchRespJson(apiUrl);
}

async function getExtGamUprPriceTargetingCandidates({ networkId }) {
  let apiUrl = `${api}/network/${networkId}/upr-price-targeting-candidates`;
  return await fetchRespJson(apiUrl);
}

async function getNetworkResourceMapping({ networkId }) {
  let apiUrl = `${api}/network/${networkId}/resource-mapping`;
  return await fetchRespJson(apiUrl);
}

async function triggerEnsureResources({ networkId }) {
  let apiUrl = `${api}/network/${networkId}/ensure-resources`;
  return await fetchRespJson(apiUrl);
}

async function syncCloudFlareAmpRTC({ networkId }) {
  let apiUrl = `${api}/network/${networkId}/code-snippet/sync-cloudflare-amp-rtc`;
  return await postData(apiUrl, { networkId });
}

async function createNewBuild({ networkId, packageName, packageVersion }) {
  let apiUrl = `${api}/network/${networkId}/code-snippet/builds`;
  return await postData(apiUrl, { packageName, packageVersion });
}

async function createTempBuild({
  networkId,
  packageName,
  packageVersion,
  overriddenInput,
}) {
  let apiUrl = `${api}/network/${networkId}/code-snippet/temp-build`;
  return await postData(apiUrl, {
    packageName,
    packageVersion,
    overriddenInput,
  });
}

async function createBuildComparison({
  networkId,
  packageName,
  baseBuildId,
  comparisonBuildId,
}) {
  let apiUrl = `${api}/network/${networkId}/code-snippet/${baseBuildId}/build-comparison`;
  return await postData(apiUrl, {
    packageName,
    baseBuildId,
    comparisonBuildId,
  });
}

async function updateProductionReleaseTime({ networkId, buildId }) {
  let apiUrl = `${api}/network/${networkId}/code-snippet/${buildId}/production-release`;
  return await postData(apiUrl, {
    networkId,
    buildId,
  });
}

async function updateIntegrationReleaseTime({ networkId, buildId }) {
  let apiUrl = `${api}/network/${networkId}/code-snippet/${buildId}/integration-release`;
  return await postData(apiUrl, {
    networkId,
    buildId,
  });
}

async function updateBuildNotes({ networkId, buildId, notes }) {
  let apiUrl = `${api}/network/${networkId}/code-snippet/${buildId}/notes`;
  return await postData(apiUrl, {
    networkId,
    buildId,
    notes,
  });
}

async function getBuildPackages() {
  let apiUrl = `${api}/code-snippet/packages`;
  return await fetchRespJson(apiUrl);
}

async function getBuildInput({ url }) {
  let apiUrl = url;
  return await fetchRespJson(apiUrl);
}

async function getBuildDetails({ networkId, buildId }) {
  let apiUrl = `${api}/network/${networkId}/code-snippet/${buildId}/detail`;
  return await fetchRespJson(apiUrl);
}

async function getCodeSnippetBuilds({ networkId }) {
  let apiUrl = `${api}/network/${networkId}/code-snippet/builds`;
  return await fetchRespJson(apiUrl);
}

async function createPublisherFeatures({ pubId, appName, config }) {
  let apiUrl = `${api}/publisher/${pubId}/features`;
  return await postData(apiUrl, { appName, config });
}

async function getRawPublisherFeatures({ pubId }) {
  let apiUrl = `${api}/publisher/${pubId}/features`;
  return await fetchRespJson(apiUrl);
}

async function createNetworkFeatures({ networkId, appName, config }) {
  let apiUrl = `${api}/network/${networkId}/features`;
  return await postData(apiUrl, { appName, config });
}

async function getDefaultNetworkFeatures({ networkId }) {
  let apiUrl = `${api}/network/${networkId}/features/default`;
  return await fetchRespJson(apiUrl);
}

async function getRawNetworkFeatures({ networkId }) {
  let apiUrl = `${api}/network/${networkId}/features`;
  return await fetchRespJson(apiUrl);
}

async function getFeaturesSchema() {
  let apiUrl = `${api}/features/schema`;
  return await fetchRespJson(apiUrl);
}

async function getRegisteredInventorySuggestion({ networkId, params }) {
  let apiUrl = `${api}/network/${networkId}/registered-inventory-suggestion`;
  return await postData(apiUrl, { params });
}

async function getCstEstimateResource({ networkId, config }) {
  let apiUrl = `${api}/network/${networkId}/estimate-resource`;
  return await postData(apiUrl, { config });
}

async function getCustomTargetingValues({ networkId, ids }) {
  const queryString = `?ids=${ids.join(",")}`;
  let apiUrl = `${api}/network/${networkId}/custom-targeting-values${queryString}`;
  return await fetchRespJson(apiUrl);
}

async function getCustomTargetingKeys({ networkId }) {
  let apiUrl = `${api}/network/${networkId}/custom-targeting-keys`;
  return await fetchRespJson(apiUrl);
}

async function createCstConfig(params) {
  let apiUrl = `${api}/network/${params.networkId}/cst-config`;
  return await postData(apiUrl, params);
}

async function getCstConfig({ networkId }) {
  let apiUrl = `${api}/network/${networkId}/cst-config`;
  return await fetchRespJson(apiUrl);
}

async function getCstConfigV2({ networkId }) {
  let apiUrl = `${api}/v2/network/${networkId}/cst-config`;
  return await fetchRespJson(apiUrl);
}

async function getCstConfigChangeHistory({ networkId, first, after }) {
  let apiUrl = `${api}/network/${networkId}/cst-config-change-history?${
    first ? `first=${first}` : ""
  }${after ? `&after=${after}` : ""}`;
  const r = await fetchRespJson(apiUrl);
  return r;
}

async function getFirstLayerPriceSuggestion(params) {
  let apiUrl = `${api}/network/${params.networkId}/first-layer-prices-suggestion`;
  return await postData(apiUrl, params);
}

async function createNetworkUprSegmentation(params) {
  let apiUrl = `${api}/v2/network/${params.networkId}/upr-segmentation`;
  return await postData(apiUrl, params);
}

async function getInventoryRegistry() {
  let apiUrl = `${api}/cst/inventory-registry`;
  return await fetchRespJson(apiUrl);
}

async function getNetworkUprSegmentationByUnitId({ unitId }) {
  let apiUrl = `${api}/network/unit/${unitId}/upr-segmentation`;
  return await fetchRespJson(apiUrl);
}

async function getNetworkUprSegmentation({ networkId }) {
  let apiUrl = `${api}/network/${networkId}/upr-segmentation`;
  return await fetchRespJson(apiUrl);
}

async function getNetworkUprSegmentationChangeHistory({
  networkId,
  first,
  after,
}) {
  let apiUrl = `${api}/network/${networkId}/upr-segmentation-change-history?${
    first ? `first=${first}` : ""
  }${after ? `&after=${after}` : ""}`;
  const r = await fetchRespJson(apiUrl);
  return r;
}

async function getNetworkGamInfo({ networkId, isForceRefresh }) {
  let apiUrl = `${api}/network/${networkId}/gam-info`;
  if (isForceRefresh) {
    apiUrl += "?isForceRefresh=true";
  }
  return await fetchRespJson(apiUrl);
}

async function updateNetworkAdServingCost(params) {
  let apiUrl = `${api}/update-network-ad-serving-cost`;
  return await postData(apiUrl, params);
}

async function updateNetworkCustomFirstLook({
  networkId,
  customSettings,
  etag,
  notes,
}) {
  const apiUrl = `${api}/network/${networkId}/update-custom-first-look`;

  // compress!!
  const compressedData = pako.gzip(JSON.stringify(customSettings));
  return await postData(apiUrl, {
    customSettings: compressedData,
    etag,
    notes,
  });
}

async function updateNetworkCustomUpr({
  networkId,
  customSettings,
  etag,
  notes,
}) {
  const apiUrl = `${api}/v2/network/${networkId}/update-custom-upr`;

  // compress!!
  const compressedData = pako.gzip(JSON.stringify(customSettings));
  return await postData(apiUrl, {
    customSettings: compressedData,
    etag,
    notes,
  });
}

async function getUnitsByNetworkId({ networkId }) {
  let apiUrl = `${api}/network/${networkId}/units`;
  const r = await fetchRespJson(apiUrl);
  return r;
}

async function getNetworkCustomUpr({ networkId }) {
  let apiUrl = `${api}/network/${networkId}/custom-upr`;
  const r = await fetchRespJson(apiUrl);
  return r;
}

async function getNetworkCustomFirstLook({ networkId }) {
  let apiUrl = `${api}/network/${networkId}/custom-first-look`;
  const r = await fetchRespJson(apiUrl);
  return r;
}

async function getNetworkCustomFirstLookChangeHistory({
  networkId,
  first,
  after,
}) {
  let apiUrl = `${api}/network/${networkId}/custom-first-look-change-history?${
    first ? `first=${first}` : ""
  }${after ? `&after=${after}` : ""}`;
  const r = await fetchRespJson(apiUrl);
  return r;
}

async function getNetworkCustomUprChangeHistory({ networkId, first, after }) {
  let apiUrl = `${api}/network/${networkId}/custom-upr-change-history?${
    first ? `first=${first}` : ""
  }${after ? `&after=${after}` : ""}`;
  const r = await fetchRespJson(apiUrl);
  return r;
}

async function updateUnitsByStatus({ networkId, params }) {
  let apiUrl = `${api}/network/${networkId}/update-units-by-status`;
  // params: {unitIds, status}
  return await postData(apiUrl, params);
}

async function getUnitsByUnitIds({ networkId, unitIds }) {
  let apiUrl = `${api}/network/${networkId}/units-by-ids`;
  const r = await postData(apiUrl, { unitIds });
  return r;
}

async function getUnitsByMode({ networkId, mode }) {
  let apiUrl = `${api}/network/${networkId}/units-by-mode?mode=${mode}`;
  const r = await fetchRespJson(apiUrl);
  return r;
}

async function getNetworksInfo({ networkIds }) {
  let apiUrl = `${api}/networks/info?networkIds=${networkIds.join(",")}`;
  const r = await fetchRespJson(apiUrl);
  return r;
}

async function getNetworkInfo({ networkId }) {
  let apiUrl = `${api}/network/${networkId}/info`;
  const r = await fetchRespJson(apiUrl);
  return r;
}

async function getNetworkResources({ networkId }) {
  let apiUrl = `${api}/network/${networkId}/resources`;
  const r = await fetchRespJson(apiUrl);
  return r;
}
async function getNetworks(params) {
  let apiUrl = `${api}/networks`;
  return await postData(apiUrl, params);
}

async function getYieldSets(params) {
  let apiUrl = `${api}/yield-sets`;
  return await postData(apiUrl, params);
}

async function validateCustomUprSetting({ setting }) {
  const apiUrl = `${api}/validate-custom-upr-setting`;
  const compressedData = pako.gzip(JSON.stringify(setting));
  return await postData(apiUrl, { setting: compressedData });
}

// async function updateNetworkCustomUpr({ networkId, customSettings, etag }) {
//   const apiUrl = `${api}/v2/network/${networkId}/update-custom-upr`;

//   // compress!!
//   const compressedData = pako.gzip(JSON.stringify(customSettings));
//   return await postData(apiUrl, { customSettings: compressedData, etag });
// }

async function validateCustomFirstLookSetting({ setting }) {
  const apiUrl = `${api}/validate-custom-first-look-setting`;
  const compressedData = pako.gzip(JSON.stringify(setting));
  return await postData(apiUrl, { setting: compressedData });
}

async function validateCstConfig({ networkId, setting }) {
  const apiUrl = `${api}/network/${networkId}/validate-cst-config`;
  const compressedData = pako.gzip(JSON.stringify(setting));
  return await postData(apiUrl, { setting: compressedData });
}

async function validateUprSegmentation({ networkId, setting }) {
  const apiUrl = `${api}/network/${networkId}/validate-upr-segmentation`;
  const compressedData = pako.gzip(JSON.stringify(setting));
  return await postData(apiUrl, { setting: compressedData });
}

// params: { networkId, yieldSetId (optional), startDate, endDate }
async function getNetworkSnapshotTrend(params) {
  const apiUrl = `${api}/network/${params.networkId}/snapshot-trend`;
  return await postData(apiUrl, params);
}
